/**
 * Composant Navbar réutilisable
 * 
 * @example
 * // Utilisation basique
 * import { Navbar } from '@/components/Navbar';
 * 
 * function MaPage() {
 *   return (
 *     <div>
 *       <Navbar />
 *       // ... contenu de votre page
 *     </div>
 *   );
 * }
 * 
 * @example
 * // Avec personnalisation
 * <Navbar backgroundColor="#f3f4f6" showAnimations={false} />
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, X, Menu } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

// Images
import logoSvg from '../assets/focuspips.svg';
import logoSvgWebP from '../assets/webp-landing/focuspips.webp';

// Composant FastImage pour optimisation
const FastImage = React.memo(({ src, srcWebP, alt, className, priority = false }: { src: string; srcWebP?: string; alt: string; className: string; priority?: boolean }) => { 
  if (srcWebP) {
    return (
      <picture>
        <source srcSet={srcWebP} type="image/webp" />
        <img 
          src={src} 
          alt={alt} 
          className={className} 
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
        />
      </picture>
    );
  }
  return <img src={src} alt={alt} className={className} loading={priority ? 'eager' : 'lazy'} />;
});

FastImage.displayName = 'FastImage';

interface NavbarProps {
  /** Couleur de fond personnalisée (optionnel, défaut: 'white') */
  backgroundColor?: string;
  /** Afficher les animations (par défaut: true) */
  showAnimations?: boolean;
  /** Position CSS (optionnel, défaut: 'fixed') */
  position?: 'fixed' | 'relative' | 'sticky' | 'static';
  /** Position top (optionnel, défaut: 0) */
  top?: string | number;
}

export const Navbar: React.FC<NavbarProps> = ({ 
  backgroundColor = 'white',
  showAnimations = true,
  position = 'fixed',
  top = 0
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/');
    } catch (error) {
      // console.error supprimé
    }
  };

  return (
    <header 
      className={`${position} left-0 right-0 z-50`} 
      style={{ 
        backgroundColor: backgroundColor,
        top: top,
        paddingTop: 'env(safe-area-inset-top)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <div className="max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-24">
        <div className="flex items-center justify-between py-2 sm:py-0">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/">
              <FastImage 
                src={logoSvg} 
                srcWebP={logoSvgWebP}
                alt="FocusPips" 
                className="h-20 sm:h-16 lg:h-20 xl:h-22 2xl:h-24 w-auto object-contain"
                priority={true}
              />
            </Link>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Menu hamburger - visible uniquement sur mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 sm:hidden transition-all duration-300 hover:scale-105 active:scale-95"
              aria-label="Menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-900" />
              ) : (
                <Menu className="h-6 w-6 text-gray-900" />
              )}
            </button>

            {/* Liens desktop */}
            <Link 
              to="/blackfriday" 
              className="px-3 py-2 font-bold transition-colors text-sm sm:text-base hidden sm:inline-flex"
              style={{
                ...(showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.2s both' } : {}),
                background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              OFFRE BLACK FRIDAY
            </Link>
            <Link 
              to="/help" 
              className="px-3 py-2 text-gray-900 hover:text-gray-600 transition-colors text-sm sm:text-base hidden sm:inline-flex"
              style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.3s both' } : {}}
            >
              FAQ
            </Link>
            <Link 
              to="/brokers" 
              className="px-3 py-2 text-gray-900 hover:text-gray-600 transition-colors text-sm sm:text-base hidden sm:inline-flex"
              style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.4s both' } : {}}
            >
              Plateformes supportées
            </Link>
            <Link 
              to="/pricing" 
              className="px-3 py-2 text-gray-900 hover:text-gray-600 transition-colors text-sm sm:text-base hidden sm:inline-flex"
              style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.5s both' } : {}}
            >
              Tarifs
            </Link>
            <Link 
              to="/blog" 
              className="px-3 py-2 text-gray-900 hover:text-gray-600 transition-colors text-sm sm:text-base hidden sm:inline-flex"
              style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.6s both' } : {}}
            >
              Blog
            </Link>
            <Link 
              to="/affiliate" 
              className="px-3 py-2 text-gray-900 hover:text-gray-600 transition-colors text-sm sm:text-base hidden sm:inline-flex"
              style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.5s both' } : {}}
            >
              Affiliation
            </Link>

            {/* Boutons utilisateur ou authentification */}
            {user ? (
              <>
                <button
                  onClick={handleLogout}
                  className="p-1.5 sm:p-2 rounded-full hover:bg-gray-200 transition-colors hidden sm:flex items-center justify-center"
                  title="Se déconnecter"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: '#111827' }}>
                    <path d="m16 17 5-5-5-5"/>
                    <path d="M21 12H9"/>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  </svg>
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="bento-button hidden sm:inline-flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base px-3 py-2 sm:px-4 sm:py-2.5"
                  style={{
                    background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                    color: 'white'
                  }}
                >
                  Dashboard
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-3 py-2 sm:px-4 sm:py-2.5 text-gray-900 hover:text-gray-600 transition-colors hidden sm:inline-flex items-center justify-center text-sm sm:text-base"
                  style={showAnimations ? { animation: 'fadeInUp 0.8s ease-out 0.7s both' } : {}}
                >
                  Connexion
                </Link>
                <Link
                  to="/register"
                  className="bento-button hidden sm:inline-flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base px-3 py-2 sm:px-4 sm:py-2.5"
                  style={{
                    background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                    animation: showAnimations ? 'fadeInUp 0.8s ease-out 0.9s both' : undefined,
                    color: 'white'
                  }}
                >
                  <LogIn className="h-3 w-3 sm:h-4 sm:w-4" />
                  Essai gratuit
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Menu mobile */}
      <div 
        className={`sm:hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`} 
        style={{ backgroundColor: backgroundColor }}
      >
        <nav className="px-4 py-1">
          <Link
            to="/login"
            className={`block px-3 py-1.5 text-gray-900 hover:bg-gray-200/50 rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.1s' : '0s' }}
          >
            Se connecter
          </Link>
          <Link
            to="/brokers"
            className={`block px-3 py-1.5 text-gray-900 hover:bg-gray-200/50 rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.2s' : '0s' }}
          >
            Plateformes supportés
          </Link>
          <Link
            to="/blackfriday"
            className={`block px-3 py-1.5 font-bold hover:bg-gray-200/50 rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{
              transitionDelay: mobileMenuOpen ? '0.3s' : '0s',
              background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            OFFRE BLACK FRIDAY
          </Link>
          <Link
            to="/help"
            className={`block px-3 py-1.5 text-gray-900 hover:bg-gray-200/50 rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.4s' : '0s' }}
          >
            FAQ
          </Link>
          <Link
            to="/pricing"
            className={`block px-3 py-1.5 text-gray-900 hover:bg-gray-200/50 rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.5s' : '0s' }}
          >
            Tarifs
          </Link>
          <Link
            to="/blog"
            className={`block px-3 py-1.5 text-gray-900 hover:bg-gray-200/50 rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.6s' : '0s' }}
          >
            Blog
          </Link>
          <Link
            to="/affiliate"
            className={`block px-3 py-1.5 text-gray-900 hover:bg-gray-200/50 rounded transition-all duration-500 ${
              mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
            }`}
            onClick={() => setMobileMenuOpen(false)}
            style={{ transitionDelay: mobileMenuOpen ? '0.6s' : '0s' }}
          >
            Affiliation
          </Link>
          {user ? (
            <>
              <button
                onClick={() => {
                  navigate('/dashboard');
                  setMobileMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 text-gray-900 hover:bg-gray-200/50 rounded transition-all duration-500 ${
                  mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                }`}
                style={{ transitionDelay: mobileMenuOpen ? '0.6s' : '0s' }}
              >
                Tableau de bord
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 text-gray-900 hover:bg-gray-200/50 rounded transition-all duration-500 flex items-center gap-2 ${
                  mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                }`}
                style={{ transitionDelay: mobileMenuOpen ? '0.7s' : '0s' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-black">
                  <path d="m16 17 5-5-5-5"/>
                  <path d="M21 12H9"/>
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                </svg>
                Se déconnecter
              </button>
            </>
          ) : (
            <Link
              to="/register"
              className={`block px-3 py-1.5 text-gray-900 hover:bg-gray-200/50 rounded transition-all duration-500 ${
                mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
              }`}
              onClick={() => setMobileMenuOpen(false)}
              style={{ transitionDelay: mobileMenuOpen ? '0.6s' : '0s' }}
            >
              S'inscrire
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
};

